/* eslint-disable react/no-unused-state,react/jsx-props-no-spreading */
import React, { Component } from 'react';
import { BrowserRouter, Switch, Route } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import qs from 'query-string';
import StoreProvider from './StoreProvider';
import {
  Shrinkable,
  Scroller,
  MessageScreen,
  Spinner,
} from './base';
import { Viewport } from './base/styled';

import asyncComponent from './asyncComponent';
import DashboardIndex from './DashboardIndex';
import Login from './Login';
// Pull this one in asynchronously because it uses react-virtualized: import Logs from './Logs';
import JqlConsole from './JqlConsole';
import dataSubscriber from './dataSubscriber';
import variables from '../variables';

import './App.scss';

const renderMergedProps = (component, ...rest) => React.createElement(component, Object.assign({}, ...rest));

const numPixels = (str) => parseInt(str.match(/\d+/)[0], 10); // e.g. '1080px' -> 1080

const PropsRoute = ({
  component,
  scrollable = false,
  minWidth = variables.minWidth,
  minHeight = variables.minHeight,
  connected,
  ...rest
}) => {
  const propsRoute = (
    <Route
      {...rest}
      render={
        (routeProps) => (
          <Shrinkable minWidth={numPixels(minWidth)} minHeight={numPixels(minHeight)}>
            <Viewport minWidth={minWidth} minHeight={minHeight} dimmed={!connected} scrollable={scrollable}>
              {renderMergedProps(component, routeProps, rest)}
            </Viewport>
          </Shrinkable>
        )
      }
    />
  );
  return propsRoute;
};

class App extends Component {

  state = {
    user: null,
    dashboards: [],
    connected: false,
  };

  async componentDidMount() {
    if (typeof window.process === 'object' && window.process.type === 'renderer') {
      // This is an Electron renderer process running on the server (localhost).
      // It will wait for a dashboard to finish loading, then it will unsubscribe from the server,
      // capture the screen, and close, so it needs unfettered access to all dashboards.
      const response = await fetch(
        '/sys/unrestricted',
        { method: 'GET' },
      );
      if (response) {
        const info = await response.json();
        if (info) {
          this.setState({ user: null, dashboards: info.dashboards, connected: true });
        }
      }
      return;
    }

    // Check for a query string parameter called "chromebox", sent by a Chromebox.
    const { chromebox = null } = qs.parse(window.location.search);
    if (chromebox) {
      const opts = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        mode: 'same-origin',
        credentials: 'include',
        body: JSON.stringify({ chromebox }),
      };
      const response = await fetch('/sys/login', opts);
      if (response) {
        const json = await response.json();
        if (!json.error) {
          const {
            user,
            dashboards = [],
          } = json;
          this.setState({ user, dashboards });
        }
      }
    }
    try {
      const response = await fetch(
        '/sys/validate',
        {
          method: 'GET',
          credentials: 'include',
        },
      );
      if (response) {
        if (response.ok) {
          const info = await response.json();
          if (info) {
            // eslint-disable-next-line react/no-did-mount-set-state
            this.setState({
              user: info.user,
              dashboards: info.dashboards,
            });
          } else {
            console.error('/sys/validate: Cannot parse token validation response.');
          }
        } else {
          console.error(`Received ${response.status} from /sys/validate: $response.statusText}`);
          const text = await response.text();
          console.log(`Response body from /sys/validate:\n${text}`);
        }
      } else {
        console.log('/sys/validate: no response.');
      }
    } catch (e) {
      console.error(e);
      console.log('Not logged in.');
    }
  }

  connectIO(sysSocket) {

    let serverInfo = null;

    const doF5 = () => {
      if (sysSocket) {
        try {
          sysSocket.disconnect();
        } catch (e) {
          console.log('Error closing sys socket.');
        }
      }
      if (window.logSocket) {
        try {
          window.logSocket.disconnect();
        } catch (e) {
          console.log('Error closing log socket.');
        }
      }
      if (window.dataSocket) {
        try {
          window.dataSocket.disconnect();
        } catch (e) {
          console.log('Error closing data socket.');
        }
      }
      window.location.reload(true);
    };

    sysSocket.on('connect', () => {
      console.log('sysSocket CONNECT');
      this.setState({ connected: true });
    });
    sysSocket.on('disconnect', () => {
      console.log('sysSocket DISCONNECT');
      this.setState({ connected: false });
    });
    sysSocket.on('reconnecting', () => {
      console.log('sysSocket RECONNECTING');
      this.setState({ connected: false });
    });
    sysSocket.on('reconnect_failed', () => {
      console.log('sysSocket FAILED');
      this.setState({ connected: false });
    });
    sysSocket.on('error', (_err) => {
      console.log('sysSocket ERROR');
      this.setState({ connected: false });
    });
    sysSocket.on('serverinfo', (newServerInfo) => {
      if (serverInfo && newServerInfo.startTime > serverInfo.startTime) {
        doF5();
      }
      serverInfo = newServerInfo;
    });
    sysSocket.on('reload', doF5);
  }

  render() {
    const app = this;
    const store = {
      get user() {
        return app.state.user;
      },
      setCredentials(user, dashboards) {
        app.setState({
          user,
          dashboards,
        });
      },
    };

    const dashboards = this.state.dashboards;
    const AsyncLog = asyncComponent(() => import('./Logs'));
    return (
      <ThemeProvider theme={variables}>
        <StoreProvider store={store}>
          {
            (dashboards && dashboards.length > 0)
              ? (
                <BrowserRouter>
                  <Switch>
                    <PropsRoute
                      exact
                      path="/"
                      key="/"
                      component={DashboardIndex}
                      scrollable
                      connected={this.state.connected}
                      params={dashboards}
                    />
                    <PropsRoute
                      exact
                      path="/login"
                      key="/login"
                      component={Login}
                      connected={this.state.connected}
                    />
                    <PropsRoute
                      exact
                      path="/log"
                      key="/log"
                      component={AsyncLog}
                      connected={this.state.connected}
                    />
                    <PropsRoute
                      exact
                      path="/jql-console"
                      key="/jql-console"
                      component={JqlConsole}
                      connected={this.state.connected}
                    />
                    {
                      dashboards.filter((entry) => (entry.component !== 'Scroller')).map((entry) => (
                        <PropsRoute
                          exact
                          path={'/' + entry.path}
                          key={'/' + entry.path}
                          localProps={entry.localProps}
                          remoteProps={entry.remoteProps}
                          minWidth={entry.metadata.minWidth ? `${entry.metadata.minWidth}px` : variables.minWidth}
                          minHeight={entry.metadata.minHeight ? `${entry.metadata.minHeight}px` : variables.minHeight}
                          component={dataSubscriber(entry)}
                          connected={this.state.connected}
                        />
                      ))
                    }
                    {
                      dashboards.filter((entry) => (entry.component === 'Scroller')).map((entry) => (
                        <PropsRoute
                          exact
                          path={'/' + entry.path}
                          key={'/' + entry.path}
                          message="pane value"
                          component={() => (
                            <Scroller
                              links={entry.localProps.panes}
                              minWidth={numPixels(variables.minWidth)}
                              cycleDurationMilliseconds={entry.localProps.cycleDurationMilliseconds}
                            >
                              {
                                entry.localProps.panes.map((pane, index) => React.createElement(
                                  dataSubscriber(dashboards.find((d) => (d.path === pane)), index),
                                ))
                              }
                            </Scroller>
                          )}
                          connected={this.state.connected}
                        />
                      ))
                    }
                    <Route
                      render={
                        (_routeProps) => renderMergedProps(MessageScreen, { message: 'There is nothing here.' })
                      }
                    />
                  </Switch>
                </BrowserRouter>
              ) : <Spinner />
          }
        </StoreProvider>
      </ThemeProvider>
    );
  }
}

export default App;
